"use client"

import { storageAvailable } from "@local/utils/src/storageAvailable"

const CACHE_KEY = "swr-cache"
const DEFAULT_TTL = 30 * 60 * 1000

interface CacheEntry<T = unknown> {
  data: T
  timestamp: number
}

type InternalCacheMap = Map<string, CacheEntry>

export const createLocalStorageCacheProvider = (ttl: number = DEFAULT_TTL) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (): Map<string, any> => {
    if (typeof window === "undefined") {
      return new Map()
    }

    const isStorageAvailable = storageAvailable()
    const now = Date.now()

    let initialEntries: [string, CacheEntry][] = []

    if (isStorageAvailable) {
      try {
        const stored = localStorage.getItem(CACHE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored) as [string, CacheEntry][]
          initialEntries = parsed.filter(([, entry]) => {
            return now - entry.timestamp < ttl
          })
        }
      } catch (error) {
        console.warn(
          "[SWR Cache] Failed to restore cache from localStorage:",
          error
        )
      }
    }

    const internalMap: InternalCacheMap = new Map(initialEntries)

    const persistCache = () => {
      if (!isStorageAvailable) return

      try {
        const entries = Array.from(internalMap.entries())
        localStorage.setItem(CACHE_KEY, JSON.stringify(entries))
      } catch (error) {
        console.warn(
          "[SWR Cache] Failed to persist cache to localStorage:",
          error
        )
      }
    }

    window.addEventListener("beforeunload", persistCache)

    const intervalId = setInterval(persistCache, 5000)

    window.addEventListener("pagehide", () => {
      clearInterval(intervalId)
      persistCache()
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const proxyMap = new Map<string, any>()

    proxyMap.get = (key: string): unknown => {
      const entry = internalMap.get(key)
      if (!entry) return undefined

      if (Date.now() - entry.timestamp >= ttl) {
        internalMap.delete(key)
        return undefined
      }

      return entry.data
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    proxyMap.set = (key: string, value: any): Map<string, any> => {
      const entry: CacheEntry = {
        data: value,
        timestamp: Date.now(),
      }
      internalMap.set(key, entry)
      return proxyMap
    }

    proxyMap.delete = (key: string): boolean => {
      return internalMap.delete(key)
    }

    proxyMap.has = (key: string): boolean => {
      const entry = internalMap.get(key)
      if (!entry) return false
      if (Date.now() - entry.timestamp >= ttl) {
        internalMap.delete(key)
        return false
      }
      return true
    }

    proxyMap.clear = (): void => {
      internalMap.clear()
    }

    Object.defineProperty(proxyMap, "size", {
      get: () => internalMap.size,
    })

    return proxyMap
  }
}

export const localStorageCacheProvider = createLocalStorageCacheProvider()

export const isCacheFresh = (
  key: string,
  ttl: number = DEFAULT_TTL
): boolean => {
  if (typeof window === "undefined") return false

  try {
    const stored = localStorage.getItem(CACHE_KEY)
    if (!stored) return false

    const entries = JSON.parse(stored) as [string, CacheEntry][]
    const entry = entries.find(([k]) => k === key)
    if (!entry) return false

    return Date.now() - entry[1].timestamp < ttl
  } catch {
    return false
  }
}

export const getCachedData = <T>(
  key: string,
  ttl: number = DEFAULT_TTL
): T | null => {
  if (typeof window === "undefined") return null

  try {
    const stored = localStorage.getItem(CACHE_KEY)
    if (!stored) return null

    const entries = JSON.parse(stored) as [string, CacheEntry<T>][]
    const entry = entries.find(([k]) => k === key)
    if (!entry) return null

    if (Date.now() - entry[1].timestamp >= ttl) {
      return null
    }

    return entry[1].data
  } catch {
    return null
  }
}
